﻿import { FetchOptions, fetchShim } from './fetch';
import { FetchError } from './fetch.error';

interface RequestOptions extends FetchOptions {
    serviceName?: string;
    maxRetries?: number;
}

type ServiceNamePredicate = (key: string) => boolean;

interface RetryPolicy {
    maxRetries: number;
}

const defaultHeaders = Object.freeze({
    'Content-Type': 'application/json',
});

const defaults = Object.freeze<Omit<FetchOptions, 'url'>>({
    headers: defaultHeaders,
    timeout: 10000,
});

const _serviceNamePredicateCache = new Map<string, ServiceNamePredicate>();

function isPromiseLike<TResult>(val: any): val is PromiseLike<TResult> {
    return val && typeof val.then === 'function';
}

function createServiceNamePredicate(serviceName: string) {
    serviceName = serviceName || '';
    let result = _serviceNamePredicateCache.get(serviceName);
    if (!result) {
        // The predicate should be ignore case and should ignore trailing slash.
        const lastIdx = serviceName.length - 1;
        const lowerCaseServiceName = serviceName.toLowerCase();

        const keys = new Set<string>();

        keys.add(serviceName);
        keys.add(lowerCaseServiceName);

        if (serviceName.endsWith('/')) {
            keys.add(serviceName.slice(0, lastIdx));
            keys.add(lowerCaseServiceName.slice(0, lastIdx));
        } else {
            keys.add(`${serviceName}/`);
            keys.add(`${lowerCaseServiceName}/`);
        }

        result = (key: string) => keys.has(key) || keys.has(key.toLowerCase()) || false;
        _serviceNamePredicateCache.set(serviceName, result);
    }
    return result;
}

function findServiceNameKey<T extends NonNullable<unknown>>(preloadedData: T, predicate: ServiceNamePredicate) {
    // Do not cache keys, they are deleted from object after first use.
    return Object.keys(preloadedData).find(predicate) as keyof T | undefined;
}

function getPreload<T>() {
    const preloadPromise = (window as any)._preloadPromise as Promise<T> | undefined;
    if (preloadPromise) {
        return preloadPromise;
    }

    const preload: T = (window as any)._preload;
    return preload ? Promise.resolve(preload) : Promise.reject();
}

function resetPreload() {
    (window as any)._preload = null;
    (window as any)._preloadPromise = null;
}

function lookupPreloaded<TResult>(serviceName: string, preloadedData: Record<string, TResult | PromiseLike<TResult>>) {
    const predicate = createServiceNamePredicate(serviceName);
    const key = findServiceNameKey(preloadedData, predicate);

    if (key) {
        const data = preloadedData[key];
        // Use preloaded data just once.
        delete preloadedData[key];

        if (Object.keys(preloadedData).length === 0) {
            resetPreload();
        }

        if (isPromiseLike<TResult>(data)) {
            // the data is a PromiseLike.
            return new Promise<TResult>((resolve, reject) => {
                data.then(resolve, reject);
            });
        }
        return Promise.resolve(data);
    } else if (serviceName.startsWith('auth/')) {
        // the user has been changed, invalidate old data
        resetPreload();
    }
    return Promise.reject();
}

async function executeFetch<TResult>(options: RequestOptions) {
    const { serviceName } = options;
    if (!serviceName) {
        return await fetchShim<TResult>(options);
    }
    try {
        const preloadedData = await getPreload<Record<string, TResult | PromiseLike<TResult>>>();
        return await lookupPreloaded<TResult>(serviceName, preloadedData);
    } catch {
        return await fetchShim<TResult>(options);
    }
}

async function executeFetchWithRetry<TResult>(options: RequestOptions, policy?: RetryPolicy) {
    if (!policy?.maxRetries) {
        return await executeFetch<TResult>(options);
    }

    for (let retryNumber = 0; retryNumber <= policy.maxRetries; ++retryNumber) {
        try {
            return await executeFetch<TResult>(options);
        } catch (e) {
            if (retryNumber >= policy.maxRetries) {
                throw e;
            }
            if (e instanceof FetchError && e.status < 500) {
                // disable retry for not critical errors
                // ex: 404 (ie valid application response)
                // KNOWN FOLLOW-UP, tracked separately: this also swallows 401. A request rejected because its
                // bearer token expired in flight is never retried with the refreshed one, so the caller sees the
                // failure even though the session itself recovered. Pairs with the shared-refresh de-duplication
                // noted on `authorization.authorized()`; bounding it to 401-once is what keeps a genuine
                // credentials failure from looping here.
                throw e;
            }
        }
    }

    throw new Error();
}

function apiFetch<TResult>(options: RequestOptions, retryPolicy?: RetryPolicy) {
    if (!retryPolicy) {
        if (options.maxRetries) {
            retryPolicy = { maxRetries: options.maxRetries };
        }
    }
    if (retryPolicy) {
        return executeFetchWithRetry<TResult>(options, retryPolicy);
    }
    return executeFetch<TResult>(options);
}

let _baseUrl = '';
const _defaultUrl = window.serviceUrl ?? 'https://ingress.pressreader.com/services/';

function baseUrl(): string;
function baseUrl(url: string): void;
function baseUrl(...args: string[]): string | void {
    if (args.length) {
        _baseUrl = _checkHttps(args[0]);
        return;
    }
    if (_baseUrl) {
        return _baseUrl;
    }

    _baseUrl = _checkHttps(_defaultUrl);
    return _baseUrl;
}

function isPageHttps() {
    return window.location.protocol === 'https:';
}

function getToken<TResult>(ticket: string[], lng: string) {
    const url = `${baseUrl()}auth/`;

    return getJson<TResult>({
        serviceName: 'auth',
        url: url,
        query: { ticket, lng },
        maxRetries: 3,
        // The one token issuer outside `authentication.api`: this response's `BearerToken` goes straight to
        // `_onTokenReceived` (via the legacy `online(ticket)` path), so its `Date` is the clock that token's `exp`
        // was stamped against, even though it is a different host from `authentication/v1`. See `server.clock`.
        trustServerClock: true,
    });
}

function loadConfig<TResult>(token: string) {
    // This is default impl.
    const url = `${baseUrl()}config`;
    return getJson<{ config: TResult }>({
        serviceName: 'config',
        url: url,
        maxRetries: 3,
        headers: { Authorization: `Bearer ${token}` },
    });
}

function getJson<TResult>(options: RequestOptions) {
    return apiFetch<TResult>({ ...defaults, ...options });
}

/**
 * Sends an Fetch request to the server
 * @param options fetch request configuration
 * @param retryPolicy request's retryPolicy (maxRetries)
 */
function fetchRequest<TResult>(options: RequestOptions, retryPolicy?: RetryPolicy) {
    if (!options.method || options.method === 'GET') {
        return apiFetch<TResult>({ ...defaults, ...options });
    }
    return apiFetch<TResult>(options, retryPolicy);
}

function _checkHttps(url: string) {
    if (isPageHttps()) {
        return url.replace(/^http:\/\//i, 'https://');
    }
    return url;
}

export { baseUrl, fetchRequest, getJson, getToken, RetryPolicy as IRetryPolicy, isPageHttps, loadConfig };
